import React from "react";
import { FieldValues } from "react-hook-form";
import { TreeSelect as AntTreeSelect } from "antd5";

import { BuyerCategory, useBuyerCategories } from "lib/hooks/api/buyer/useBuyerCategories";
import { TreeSelect, TreeSelectProps } from "./Inputs";

type TreeData = { value: string; label: string; children: TreeData[] };
function getChildren(data: BuyerCategory[], parentId: string): TreeData[] {
  const children = data.filter((cat) => cat.parentCategoryId === parentId);
  return children.map((cat) => ({
    value: cat.id,
    label: cat.name,
    children: getChildren(data, cat.id),
  }));
}

type FieldProps<T extends FieldValues> = Omit<TreeSelectProps<T>, "treeData">;
export function BuyerTypeSelect<T extends FieldValues>(props: FieldProps<T>) {
  const { data, isLoading, isError } = useBuyerCategories();

  const treeData =
    data
      ?.filter((cat) => !cat.parentCategoryId)
      .map((cat) => {
        const children = getChildren(data, cat.id);
        return { value: cat.id, label: cat.name, children };
      }) || [];

  return (
    <TreeSelect
      treeData={treeData}
      treeCheckable={true}
      treeNodeFilterProp="label"
      showSearch
      showCheckedStrategy={AntTreeSelect.SHOW_PARENT}
      loading={isLoading}
      {...props}
      // After the spread on purpose, so the failure state can't be masked by a caller's
      // placeholder. Without this the tree renders empty, which reads as "this buyer type has no
      // options" rather than "the request failed".
      {...(isError ? { status: "error" as const, placeholder: "Couldn't load buyer types" } : {})}
    />
  );
}
